/**
 * The native session credential store.
 *
 * WHY THIS EXISTS. The web client authenticates with the httpOnly `chompdome_session` cookie and
 * always will. A Capacitor shell cannot, and not merely as an inconvenience:
 *
 * - a bundled build is cross-site — the page is `https://localhost` (Android) or
 *   `capacitor://localhost` (iOS) and the API is somewhere else entirely — so the cookie is a
 *   third-party cookie a WebView is entitled to refuse; and
 * - Capacitor's iOS cookie layer MANGLES `Set-Cookie`, splitting the header on commas and
 *   shredding any cookie carrying a date (ionic-team/capacitor#6280, unfixed since 2023).
 *
 * Weakening `SameSite` to `none` was considered and rejected: it would trade real CSRF posture
 * on the web for something that still does not work on iOS.
 *
 * WHAT CHANGES, AND WHAT DOES NOT. The credential is the **same opaque `user_sessions.id`** the
 * cookie would have carried — not a JWT, not a second token type. Only its envelope differs: the
 * server returns it in `X-Session-Id`, this module keeps it in the Keychain (iOS) / Keystore
 * (Android), and it goes back as `Authorization: Bearer <id>`. The server routes that into the
 * same `resolveSession` path the cookie uses, so revocation, the session cache and the expiry
 * checks are untouched and custody of the credential stays server-side.
 *
 * WHY `@aparajita/capacitor-secure-storage`. It ships a `Package.swift`, and `apps/web/ios` is an
 * SPM project with no Podfile. A CocoaPods-only plugin (`capacitor-native-biometric` is the one
 * that bit trade-loop) is *silently omitted* by `npx cap sync` on an SPM project — the plugin is
 * simply not in the iOS binary and every call rejects as unimplemented, which presents as a
 * Keychain bug rather than a missing dependency. `pnpm mobile:sync` prints the plugin list; both
 * platforms must name this one.
 */

import { SecureStorage, StorageError, StorageErrorType } from '@aparajita/capacitor-secure-storage';
import {
  CLIENT_PLATFORM_HEADER,
  NATIVE_CLIENT_PLATFORM,
  SESSION_ID_HEADER,
} from '@chompdome/shared/constants';
import { isNativePlatform } from './platform';

/** Namespaced so it cannot collide with anything else this app ever puts in the secure store. */
const SESSION_KEY = 'chompdome.session.id';

/**
 * The outcome of a restore attempt. Four genuinely different states, kept apart on purpose:
 * collapsing them is what lets a missing native plugin masquerade as "the player was simply
 * signed out". `unavailable` means the app is misconfigured, not that anyone needs to sign in.
 */
export type RestoreResult =
  | { status: 'restored'; sessionId: string }
  | { status: 'missing' }
  | { status: 'unavailable'; error: Error }
  | { status: 'corrupt'; error: Error };

/**
 * Read-through cache. Every outgoing request needs the id and the tRPC link's `fetch` is
 * synchronous up to the point it builds its headers, so it cannot await the Keychain.
 * `restoreSessionId()` fills this once at startup.
 */
let cachedSessionId: string | null = null;

/**
 * Set when the secure store itself is broken (plugin missing, OS error) — as opposed to simply
 * holding no session. Kept because a store failure is an integration bug nobody can otherwise
 * see, and treating it as "signed out" is exactly what hides it.
 */
let storeFailure: string | null = null;

export function getStoreFailure(): string | null {
  return storeFailure;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/** The current session id, or null. Synchronous — safe to call per request. */
export function getSessionId(): string | null {
  return cachedSessionId;
}

/**
 * Load any persisted session id into the cache. Called once before the first request, so a
 * returning player stays signed in across an app restart.
 *
 * `SecureStorage.get()` resolves `null` for a key that is genuinely absent and *throws* for a
 * real failure, so the two are distinguishable here without reading error messages.
 */
export async function restoreSessionId(): Promise<RestoreResult> {
  if (!isNativePlatform()) return { status: 'missing' };

  try {
    const stored = await SecureStorage.get(SESSION_KEY);
    storeFailure = null;

    if (stored === null || stored === undefined) {
      cachedSessionId = null;
      return { status: 'missing' };
    }
    if (typeof stored !== 'string' || stored.length === 0) {
      cachedSessionId = null;
      const error = new Error(`Stored session was ${typeof stored}, expected a non-empty string`);
      console.error('[NativeSession] Stored session is corrupt:', error.message);
      return { status: 'corrupt', error };
    }

    cachedSessionId = stored;
    return { status: 'restored', sessionId: stored };
  } catch (caught) {
    cachedSessionId = null;
    const error = toError(caught);
    const corrupt = caught instanceof StorageError && caught.code === StorageErrorType.invalidData;

    // Loud on purpose. The next missing plugin, signing problem or OS error has to announce
    // itself rather than presenting as a player who is not signed in.
    console.error(
      `[NativeSession] Secure storage ${corrupt ? 'returned corrupt data' : 'is unavailable'}:`,
      error,
    );

    if (corrupt) return { status: 'corrupt', error };
    storeFailure = error.message;
    return { status: 'unavailable', error };
  }
}

/**
 * Persist a freshly issued session id. A no-op on the web, which has the cookie.
 *
 * Deliberately does not throw: the in-memory copy means the player IS signed in for this run.
 * The failure is recorded and logged instead — losing persistence silently is the bug this file
 * is shaped to prevent.
 */
export async function setSessionId(sessionId: string): Promise<void> {
  if (!isNativePlatform()) return;
  cachedSessionId = sessionId;

  try {
    await SecureStorage.set(SESSION_KEY, sessionId);
    storeFailure = null;
  } catch (caught) {
    const error = toError(caught);
    storeFailure = error.message;
    console.error('[NativeSession] Could not persist the session to secure storage:', error);
  }
}

/**
 * Forget the session — signing out, or a session the server has told us is dead.
 *
 * This has to actually succeed. iOS Keychain entries survive an app uninstall, so a session left
 * behind can silently sign a later install back in as the previous player.
 */
export async function clearSessionId(): Promise<void> {
  cachedSessionId = null;
  if (!isNativePlatform()) return;

  try {
    await SecureStorage.remove(SESSION_KEY);
  } catch (caught) {
    console.error('[NativeSession] Could not clear the stored session:', toError(caught));
  }
}

/**
 * Capture a session id the server issued on this response, if it did.
 *
 * Native-only by construction at both ends: the server emits `X-Session-Id` only to a request
 * that carried `X-Client-Platform: native`, and this returns immediately in a browser.
 */
export async function captureSessionId(response: Response): Promise<void> {
  if (!isNativePlatform()) return;
  const issued = response.headers.get(SESSION_ID_HEADER);
  if (issued) await setSessionId(issued);
}

/**
 * The headers a native request adds, merged onto whatever the caller already had.
 *
 * Returns the caller's own headers *unchanged* in a browser — same object, same order — so the
 * web request this client has always made stays byte for byte what it was.
 */
export function withNativeAuth(headers: HeadersInit | undefined): HeadersInit | undefined {
  if (!isNativePlatform()) return headers;

  const merged = new Headers(headers);
  // Sent on every request, not only the authenticated ones: it is what asks for `X-Session-Id`
  // back, and the requests that mint a session are precisely the ones with no session yet.
  merged.set(CLIENT_PLATFORM_HEADER, NATIVE_CLIENT_PLATFORM);
  if (cachedSessionId !== null) merged.set('Authorization', `Bearer ${cachedSessionId}`);
  return merged;
}
